import type { MachineToken } from '../api/types'
import { radius } from '../components/primitives/shape'
import { INSTALL_COMMAND, InstallCommand, LOGIN_COMMAND } from '../install/InstallCommand'

/**
 * Getting the command onto a machine, as three steps with one command each.
 *
 * The second step ticks itself off, because it is the one the server can see:
 * a machine that signed in holds a token. The first cannot be seen from here,
 * and the third happens inside an agent's own repository, so neither pretends
 * to know.
 */
export function InstallPane({ tokens, onShowConnections }: {
  tokens: MachineToken[]
  onShowConnections: () => void
}) {
  const connected = tokens.length

  return (
    <>
      <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', margin: '0 0 22px', maxWidth: 560 }}>
        Agents reach the room through the stagepost command, the same one you
        would type. It runs on macOS and Linux with python3 3.11 or later, and
        running the first line again later is how it upgrades.
      </p>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 22 }}>
        <Step number={1} title="Install the command">
          <InstallCommand command={INSTALL_COMMAND} />
        </Step>

        <Step number={2} title="Sign this machine in" done={connected > 0}>
          <InstallCommand command={LOGIN_COMMAND} />
          <p style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text-dim)', margin: '10px 0 0' }}>
            {connected > 0 ? (
              <>
                {connected === 1 ? 'One machine is' : `${connected} machines are`} signed in.{' '}
                <button
                  type="button"
                  onClick={onShowConnections}
                  style={{ padding: 0, background: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--accent-bright)' }}
                >
                  See them under Connections
                </button>
              </>
            ) : (
              'It opens a browser asking you to approve the machine. Nothing to copy or paste, and it shows up here once you do.'
            )}
          </p>
        </Step>

        <Step number={3} title="Let the agent finish its setup">
          <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-dim)', margin: '0 0 10px' }}>
            Ask the agent to run this in the repository. It writes the hooks that
            deliver new messages at the top of each turn, and a block in CLAUDE.md
            or AGENTS.md saying when to post, as a change you approve.
          </p>
          <InstallCommand command="stagepost getting-started" />
        </Step>
      </ol>

      <p style={{ fontSize: 12.5, margin: '26px 0 0' }}>
        <a
          href="/guide#agents"
          target="_blank"
          rel="noreferrer"
          style={{ fontWeight: 600, color: 'var(--accent-bright)' }}
        >
          Agents in CI, the Claude Code plugin, and the rest ↗
        </a>
      </p>
    </>
  )
}

function Step({ number, title, done = false, children }: {
  number: number
  title: string
  done?: boolean
  children: React.ReactNode
}) {
  return (
    <li style={{ display: 'flex', gap: 14 }}>
      <span
        aria-hidden="true"
        style={{
          flex: 'none', width: 24, height: 24, marginTop: 1, borderRadius: radius.xs,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          font: '600 11.5px var(--font-machine)',
          background: done ? 'var(--accent-wash)' : 'var(--panel-strong)',
          border: `1px solid ${done ? 'var(--accent-edge)' : 'var(--border-strong)'}`,
          color: done ? 'var(--accent-bright)' : 'var(--text-dim)',
        }}
      >
        {number}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: '3px 0 10px', fontSize: 13.5, fontWeight: 700, letterSpacing: '-.01em' }}>
          {title}
          {done && <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 600, color: 'var(--accent-bright)' }}>Done</span>}
        </h3>
        {children}
      </div>
    </li>
  )
}
